import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRoleState] = useState(() => localStorage.getItem("truva_active_role") || null);
  const [previewAs, setPreviewAsState] = useState(() => localStorage.getItem("truva_preview_as") || "role:Manager");
  const setPreviewAs = useCallback((v) => {
    localStorage.setItem("truva_preview_as", v);
    setPreviewAsState(v);
    localStorage.removeItem("truva_active_role");
    window.location.href = v.startsWith("rm:") ? "/my-day" : "/standup";
  }, []);

  const setActiveRole = useCallback((r) => {
    setActiveRoleState(r);
    if (r) localStorage.setItem("truva_active_role", r);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setActiveRoleState((prev) => {
        if (prev && (data.roles || []).includes(prev)) return prev;
        return data.roles?.[0];
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    window.location.href = "/";
  };

  // Batch F: per-user "New look" → data-theme="v2" on <html>; nothing changes for users with it off
  const newLook = !!user?.new_look;
  useEffect(() => {
    if (newLook) document.documentElement.setAttribute("data-theme", "v2");
    else document.documentElement.removeAttribute("data-theme");
  }, [newLook]);
  const setNewLook = useCallback(async (on) => {
    await api.post("/auth/me/new-look", { on });
    setUser((u) => (u ? { ...u, new_look: on } : u));
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, activeRole, setActiveRole, newLook, setNewLook, previewAs, setPreviewAs, isViewer: !!user?.viewer }}>
      {children}
    </AuthContext.Provider>
  );
}
